import { forwardRef } from "react";
import { Link } from "wouter";
import { motion, useReducedMotion } from "framer-motion";
import { format } from "date-fns";
import { ArrowUpRight } from "lucide-react";
import { cn } from "@/lib/utils";
import type { MomentumItem } from "@/lib/momentum";
import { StepCalendarAction } from "@/components/calendar/step-calendar-action";
import { useCalendarFlow } from "@/components/calendar/calendar-flow";
import { StepAssistantChips, StepMenu } from "@/components/assistant/step-assistant";

interface Props {
  item: MomentumItem;
  onToggle: (item: MomentumItem) => void;
  /**
   * Phone layout (the Momentum sheet): the title and source wrap instead of truncating,
   * and the date drops to its own line, so nothing can push the card past the sheet.
   */
  stacked?: boolean;
}

/** One Next Step, as it sits in an open Momentum folder. */
export const MomentumCard = forwardRef<HTMLDivElement, Props>(function MomentumCard(
  { item, onToggle, stacked = false },
  ref,
) {
  const reduce = useReducedMotion();
  const flow = useCalendarFlow();
  const done = item.completed;

  const date = new Date(item.journalCreatedAt);

  return (
    <div
      ref={ref}
      className={cn(
        "group/card relative flex h-full gap-3.5 rounded-[24px] border px-4 py-4 transition-[background-color,border-color,box-shadow] duration-300 sm:gap-[15px] sm:rounded-[32px] sm:pb-[17px] sm:pl-[22px] sm:pr-[21px] sm:pt-[18px]",
        done
          ? "border-foreground/[0.04] bg-white/55"
          : "border-foreground/[0.06] bg-white shadow-[0_1px_1px_rgba(40,37,34,0.05),0_6px_9px_rgba(40,37,34,0.14)]",
      )}
    >
      <CheckCircle checked={done} reduce={!!reduce} label={item.text} onChange={() => onToggle(item)} />

      <div className="min-w-0 flex-1">
        <p
          className={cn(
            "text-[15px] leading-snug transition-colors duration-300",
            stacked && "[overflow-wrap:anywhere]",
            done ? "text-muted-foreground/60 line-through decoration-muted-foreground/35 decoration-1" : "text-foreground",
          )}
        >
          {item.text}
        </p>

        {stacked ? (
          <div className="mt-2.5 space-y-1 text-xs text-muted-foreground">
            <Link
              href={`/journal/${item.journalId}`}
              className="group/src block w-fit max-w-full rounded-md hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            >
              {/* Clamped on an inner span: overflow-hidden on the rounded link would shave the corner letters. */}
              <span className="line-clamp-2 [overflow-wrap:anywhere]">
                <span className="text-muted-foreground/70">From </span>
                <span className="text-[13px] font-medium text-foreground/80 group-hover/src:text-foreground">
                  {item.journalTitle}
                </span>
                {/* No-break space: the arrow always ends the title's last line, never sits alone. */}
                {"\u00a0"}
                <ArrowUpRight className="inline h-3 w-3 align-[-1px] opacity-50 transition-opacity group-hover/src:opacity-100" />
              </span>
            </Link>
            {!Number.isNaN(date.getTime()) && (
              <time dateTime={item.journalCreatedAt} className="block tabular-nums text-muted-foreground/80">
                {format(date, "MMM d")}
              </time>
            )}
          </div>
        ) : (
          <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-muted-foreground">
            <Link
              href={`/journal/${item.journalId}`}
              className="group/src inline-flex min-w-0 max-w-full items-center gap-1 rounded-md hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            >
              <span className="shrink-0 text-muted-foreground/70">From</span>
              <span className="truncate text-[13px] font-medium text-foreground/80 group-hover/src:text-foreground">
                {item.journalTitle}
              </span>
              <ArrowUpRight className="h-3 w-3 shrink-0 opacity-50 transition-opacity group-hover/src:opacity-100" />
            </Link>
            {!Number.isNaN(date.getTime()) && (
              <time dateTime={item.journalCreatedAt} className="shrink-0 tabular-nums text-muted-foreground/80">
                {format(date, "MMM d")}
              </time>
            )}
          </div>
        )}

        {/* The step's calendar line, then what an assistant did with it. */}
        <div className="mt-3 -ml-2 flex flex-wrap items-center gap-x-2 gap-y-1.5 empty:hidden">
          <StepCalendarAction item={item} />
          <StepAssistantChips item={item} compact={stacked} onCalendar={!!flow.schedules.get(item.id)?.events.length} />
        </div>
      </div>

      <StepMenu item={item} />
    </div>
  );
});

function CheckCircle({
  checked,
  reduce,
  label,
  onChange,
}: {
  checked: boolean;
  reduce: boolean;
  label: string;
  onChange: () => void;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={checked ? `Mark not done: ${label}` : `Mark done: ${label}`}
      onClick={onChange}
      // 44px hit area around a 22px circle
      className="-m-2.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-full outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
    >
      <motion.span
        className="flex h-[22px] w-[22px] items-center justify-center rounded-full border-2"
        initial={false}
        animate={
          checked
            ? { backgroundColor: "hsl(258 35% 50%)", borderColor: "hsl(258 35% 50%)", scale: reduce ? 1 : [1, 0.86, 1] }
            : { backgroundColor: "rgba(255,255,255,0)", borderColor: "hsl(24 8% 62%)", scale: 1 }
        }
        whileHover={checked || reduce ? undefined : { borderColor: "hsl(258 35% 50%)" }}
        transition={{ duration: reduce ? 0 : 0.22, ease: "easeOut" }}
      >
        <svg viewBox="0 0 12 12" className="h-3 w-3" fill="none" aria-hidden>
          <motion.path
            d="M2.5 6.2l2.4 2.4 4.6-5"
            stroke="white"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            initial={false}
            animate={{ pathLength: checked ? 1 : 0, opacity: checked ? 1 : 0 }}
            transition={{ duration: reduce ? 0 : 0.24, delay: checked && !reduce ? 0.06 : 0, ease: "easeOut" }}
          />
        </svg>
      </motion.span>
    </button>
  );
}
